import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { IRootState } from 'config/redux';
import { AppConstants, RouteConstants, ServiceConstants } from '../../constants';
import { CreditService } from 'services';
import { AppUtils } from 'utils';
import { CreditOptionsResponse, CreditRequestBasicForm, CreditResponse } from 'types';
import { CreditRequestFormContext } from 'context';
import { EncodedHeader, Section, Stepper } from 'components';
import { Footer } from 'components';

const {
  CREDIT_AMOUNT_INITIAL,
  CREDIT_PAYMENT_INITIAL,
  CREDIT_REQUEST_STEP_BASIC,
  CREDIT_REQUEST_STEPS,
} = AppConstants;
const { ROUTE_CREDIT_REQUEST_BASIC, ROUTE_CREDIT_REQUEST_FORM, ROUTE_CREDIT_REQUEST_DOCUMENTATION } = RouteConstants;
const { REQUEST_CREDIT_OPTIONS_ID } = ServiceConstants;

const initialValues: CreditRequestBasicForm = {
  creditAmount: CREDIT_AMOUNT_INITIAL,
  paymentPeriod: CREDIT_PAYMENT_INITIAL,
  monthlyInstallmentFrom: '',
  monthlyInstallmentTo: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
};

const stepRoutes = [
  ROUTE_CREDIT_REQUEST_BASIC,
  ROUTE_CREDIT_REQUEST_FORM,
  ROUTE_CREDIT_REQUEST_DOCUMENTATION,
]

const CreditRequestLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const creditOptionsServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_OPTIONS_ID]);

  const [options, setOptions] = useState<CreditOptionsResponse | undefined>({} as CreditOptionsResponse);
  const [activeStep, setActiveStep] = useState<number>(CREDIT_REQUEST_STEP_BASIC);
  const [currentCredit, setCurrentCredit] = useState<Partial<CreditResponse>>({} as CreditResponse);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isEditable, setIsEditable] = useState<boolean>(true);

  const form = useForm({
    initialValues,
    onValuesChange: (values, previousValues) => {
      const creditAmountChanged = values.creditAmount !== previousValues.creditAmount;
      const paymentPeriodChanged = values.paymentPeriod !== previousValues.paymentPeriod;

      if (creditAmountChanged || paymentPeriodChanged) {
        AppUtils.populateMonthlyInstallment(values.creditAmount, values.paymentPeriod, form);
      }
    }
  });

  useEffect(() => {
    CreditService.creditOptions();
    AppUtils.populateMonthlyInstallment(form.values.creditAmount, form.values.paymentPeriod, form);
  }, []);

  useEffect(() => {
    AppUtils.resolveServiceObject(creditOptionsServiceObject, REQUEST_CREDIT_OPTIONS_ID, setOptions);
  }, [creditOptionsServiceObject]);

  const handleStepChange = (val: number) => {
    navigate(`${stepRoutes[val]}${location.search}`)
  }

  if (AppUtils.isEmpty(options)) {
    return <></>;
  }

  return (
    <CreditRequestFormContext.Provider value={{ form, options, setActiveStep, currentCredit, setCurrentCredit, isEditable, setIsEditable, isSubmitted, setIsSubmitted }}>
      <Section>
        <Box w={'100%'}>
          <EncodedHeader t={t} />
          <Stepper
            numOfSteps={CREDIT_REQUEST_STEPS}
            setActiveStep={isSubmitted ? undefined : handleStepChange}
            allowedReturnToStart={false}
            titleKey={'credit.step'}
            t={t}
            activeStep={activeStep}
          />
          <Outlet />
        </Box>
      </Section>
      <Footer />
    </CreditRequestFormContext.Provider>
  )
}

export default CreditRequestLayout;